import { useCallback, useContext, useMemo, useState } from 'react';
import { ValuationAllocationModel } from 'api';
import { AUDITOR_VALUE } from 'common/constants/user';
import { useStore } from 'common/store';
import { DialogContextValues } from 'common/types/dialogContext';
import { UseStoreValues } from 'common/types/store';
import { ConfirmationDialog } from 'components/Dialogs';
import { DialogContext } from 'context';
import {
  CHANGE_OWNERSHIP_LABEL,
  CHANGE_VALUATION_OWNERSHIP_LABEL,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import ValuationOwnershipConfirmation from 'pages/ValuationsAllocation/components/ValuationOwnershipConfirmation/ValuationOwnershipConfirmation';
import { getObjectValue } from 'utilities';
import {
  FundInvestmentOwnershipId,
  UseFundInvestmentValuationOwnershipParams,
  UseFundInvestmentValuationOwnershipReturn,
} from './useFundInvestmentValuationOwnership.types';

const useFundInvestmentValuationOwnership = (
  params: UseFundInvestmentValuationOwnershipParams
): UseFundInvestmentValuationOwnershipReturn => {
  const { updateValuation, valuationByAllocation } = params;
  const { is_firm_owned: valuationIsFirmOwned } = getObjectValue(valuationByAllocation);

  const [storeValue] = useStore() as UseStoreValues;
  const { firmInfo: currentFirm, user: currentUser } = getObjectValue(storeValue);
  const { id: currentFirmId } = getObjectValue(currentFirm);
  const { firms_permissions: currentUserPermissions, is_staff: isCurrentUserStaff = false } =
    getObjectValue(currentUser);

  const { showDialog } = useContext(DialogContext) as unknown as DialogContextValues;

  const [currentFundInvestmentOwnershipId, setCurrentFundInvestmentOwnershipId] =
    useState<FundInvestmentOwnershipId>(null);

  const isCurrentValuationFirmOwned = useMemo<boolean>(() => valuationIsFirmOwned, [valuationIsFirmOwned]);

  const isCurrentUserAuditor = useMemo<boolean>(() => {
    if (!currentUserPermissions || !currentFirmId) {
      return false;
    }

    const fiirmPermission = currentUserPermissions.find(permission => permission.id === currentFirmId);

    if (!fiirmPermission) {
      return false;
    }

    return [AUDITOR_VALUE].includes(fiirmPermission.user_firm_role);
  }, [currentFirmId, currentUserPermissions]);

  // Firm users cannot edit Scalar valuation
  const isCurrentFirmUserReadOnly = useMemo<boolean>(
    () => !isCurrentUserStaff && !isCurrentValuationFirmOwned,
    [isCurrentUserStaff, isCurrentValuationFirmOwned]
  );

  const changeValuationOwnership = useCallback(() => {
    const valuationWithOwnership = {
      ...valuationByAllocation,
      is_firm_owned: !isCurrentValuationFirmOwned,
    } as ValuationAllocationModel;

    if (valuationWithOwnership) {
      updateValuation({ valuation: valuationWithOwnership });
    }
  }, [isCurrentValuationFirmOwned, updateValuation, valuationByAllocation]);

  const showValuationOwnershipDialog = useCallback(() => {
    showDialog({
      wrapper: ConfirmationDialog,
      wrapperProps: {
        title: CHANGE_VALUATION_OWNERSHIP_LABEL,
      },
      content: ValuationOwnershipConfirmation,
      actions: [
        {
          label: CHANGE_OWNERSHIP_LABEL,
          type: 'primary' as const,
          callback: changeValuationOwnership,
        },
      ],
    });
  }, [changeValuationOwnership, showDialog]);

  return {
    currentFundInvestmentOwnershipId,
    isCurrentFirmUserReadOnly,
    isCurrentUserAuditor,
    isCurrentValuationFirmOwned,
    setCurrentFundInvestmentOwnershipId,
    showValuationOwnershipDialog,
  };
};

export default useFundInvestmentValuationOwnership;
